import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, X, Star, Loader2, ChevronLeft, ChevronRight, Link2, ImageOff } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { isUrl } from "./FormKit";
import { btnOutline, inputCls } from "./ui";

const MAX_BYTES = 5 * 1024 * 1024;
const OK_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

/**
 * Multi-image uploader/gallery. value = array of URLs (first one is the main image).
 * Upload many files (click or drag & drop) or add by URL; click a thumbnail to preview; star = make main.
 */
export default function ImageGallery({ value = [], onChange, max = 8, error, compact = false }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(0);
  const [drag, setDrag] = useState(false);
  const [preview, setPreview] = useState(null);       // index or null
  const [urlText, setUrlText] = useState("");
  const [urlErr, setUrlErr] = useState("");

  const add = (urls) => onChange([...value, ...urls].slice(0, max));

  const uploadFiles = async (list) => {
    const files = Array.from(list || []);
    if (!files.length) return;
    const room = max - value.length;
    if (room <= 0) return toast.error(`You can add up to ${max} images. Remove one to add another.`);
    const accepted = [];
    for (const f of files) {
      if (!OK_TYPES.includes(f.type)) { toast.error(`${f.name}: only JPG, PNG, GIF or WebP images are allowed.`); continue; }
      if (f.size > MAX_BYTES) { toast.error(`${f.name} is larger than 5 MB.`); continue; }
      accepted.push(f);
    }
    if (accepted.length > room) toast.warning(`Only ${room} more image${room > 1 ? "s" : ""} fit${room > 1 ? "" : "s"} — extra files were skipped.`);
    const batch = accepted.slice(0, room);
    if (!batch.length) return;
    setUploading(batch.length);
    const urls = [];
    for (const f of batch) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/vendor/upload", fd);
        urls.push(data.url);
      } catch (err) {
        toast.error(`${f.name}: ${apiErr(err.response?.data?.detail)}`);
      } finally {
        setUploading((n) => Math.max(0, n - 1));
      }
    }
    if (urls.length) {
      add(urls);
      toast.success(urls.length === 1 ? "Image uploaded" : `${urls.length} images uploaded`);
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const addUrl = () => {
    const u = urlText.trim();
    if (!u) return setUrlErr("Enter an image URL.");
    if (!isUrl(u)) return setUrlErr("Enter a valid URL starting with http:// or https://");
    if (value.includes(u)) return setUrlErr("This image is already added.");
    if (value.length >= max) return setUrlErr(`Maximum ${max} images.`);
    setUrlErr(""); setUrlText(""); add([u]);
  };

  const remove = (i) => { onChange(value.filter((_, idx) => idx !== i)); setPreview(null); };
  const makeMain = (i) => { onChange([value[i], ...value.filter((_, idx) => idx !== i)]); toast.success("Main image updated"); };

  return (
    <div>
      <div onClick={() => fileRef.current?.click()} data-testid="image-gallery-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); uploadFiles(e.dataTransfer.files); }}
        className={`cursor-pointer rounded-lg border-2 border-dashed text-center transition-colors ${compact ? "px-3 py-3" : "px-3 py-4"} ${drag ? "border-blue-600 bg-blue-50" : error ? "border-red-400 bg-red-50/40" : "border-slate-300 hover:border-blue-400 hover:bg-blue-50/50"}`}>
        {uploading ? <Loader2 size={20} className="mx-auto text-blue-600 animate-spin" /> : <UploadCloud size={20} className="mx-auto text-blue-600" />}
        <div className="mt-1 text-[13px] font-semibold text-neutral-900">{uploading ? `Uploading ${uploading} image${uploading > 1 ? "s" : ""}…` : "Click to upload or drag & drop"}</div>
        <div className="text-[11px] text-slate-500">JPG, PNG, GIF or WebP · up to 5 MB each · max {max} images · select several at once</div>
      </div>
      <input ref={fileRef} type="file" multiple hidden accept={OK_TYPES.join(",")} data-testid="image-gallery-input" onChange={(e) => uploadFiles(e.target.files)} />
      {error && <p role="alert" className="mt-1 text-[11px] text-red-600">{error}</p>}

      {value.length > 0 && (
        <div className="grid grid-cols-4 gap-2 mt-2.5">
          {value.map((u, i) => (
            <div key={u} className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
              <button type="button" onClick={() => setPreview(i)} className="w-full h-full" aria-label={`Preview image ${i + 1}`}>
                <img src={u} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                <ImageOff size={16} className="absolute inset-0 m-auto text-slate-300 -z-0" />
              </button>
              {i === 0 && <span className="absolute left-1 top-1 text-[9px] font-bold bg-orange-500 text-white px-1.5 py-0.5 rounded">Main</span>}
              <div className="absolute right-1 top-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {i !== 0 && <button type="button" onClick={() => makeMain(i)} aria-label="Make main image" title="Make main" className="w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-orange-500"><Star size={11} /></button>}
                <button type="button" onClick={() => remove(i)} aria-label="Remove image" title="Remove" className="w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-600"><X size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="text-[11px] text-slate-500 mt-1.5">{value.length}/{max} images{value.length > 1 ? " · hover a thumbnail to set the main image" : ""}</div>

      <div className="flex gap-2 mt-2">
        <div className="flex-1 relative">
          <Link2 size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={urlText} onChange={(e) => { setUrlText(e.target.value); setUrlErr(""); }} placeholder="…or paste an image URL"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }} aria-invalid={urlErr ? "true" : "false"}
            className={`${inputCls} pl-8 ${urlErr ? "!border-red-500" : ""}`} data-testid="image-gallery-url" />
        </div>
        <button type="button" onClick={addUrl} className={btnOutline}>Add</button>
      </div>
      {urlErr && <p role="alert" className="mt-1 text-[11px] text-red-600">{urlErr}</p>}

      {preview !== null && value[preview] && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-6" onMouseDown={(e) => { if (e.target === e.currentTarget) setPreview(null); }}>
          <button onClick={() => setPreview(null)} aria-label="Close preview" className="absolute right-5 top-5 w-9 h-9 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"><X size={18} /></button>
          {value.length > 1 && <button onClick={() => setPreview((p) => (p - 1 + value.length) % value.length)} aria-label="Previous image" className="absolute left-5 w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"><ChevronLeft size={20} /></button>}
          <div className="max-w-3xl max-h-full flex flex-col items-center gap-3">
            <img src={value[preview]} alt="" className="max-h-[75vh] rounded-lg object-contain bg-white" />
            <div className="flex items-center gap-3 text-[12px] text-white/80">
              <span>{preview + 1} / {value.length}{preview === 0 ? " · Main image" : ""}</span>
              {preview !== 0 && <button onClick={() => { makeMain(preview); setPreview(0); }} className="px-2.5 py-1 rounded-md bg-orange-500 text-white font-semibold hover:bg-orange-600">Set as main</button>}
              <button onClick={() => remove(preview)} className="px-2.5 py-1 rounded-md bg-red-600 text-white font-semibold hover:bg-red-700">Remove</button>
            </div>
          </div>
          {value.length > 1 && <button onClick={() => setPreview((p) => (p + 1) % value.length)} aria-label="Next image" className="absolute right-5 w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"><ChevronRight size={20} /></button>}
        </div>
      )}
    </div>
  );
}
